import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  Input
} from '@angular/core';
import { environment } from '../../environments/environment';
import { getApiAuthHeaders } from '../services/session-auth.store';

export const LOG_EVENT_TYPES = [
  'response.content.done',
  'rate_limits.updated',
  'response.done',
  'input_audio_buffer.commit',
  'input_audio_buffer.speech_started',
  'input_audio_buffer.speech_stopped',
  'response.text.done'
];

@Component({
  standalone: true,
  selector: 'app-realtime-voice-agent',
  templateUrl: './realtime-voice-agent.component.html',
  styleUrls: ['./realtime-voice-agent.component.css']
})
export class RealtimeVoiceAgentComponent implements OnInit, OnDestroy {
  @ViewChild('startBtn') startBtn!: ElementRef;
  @ViewChild('stopBtn') stopBtn!: ElementRef;

  @Input() cif: string | null = null;
  @Input() currentSessionId: string | null = null;

  private ws!: WebSocket;
  private mediaRecorder!: MediaRecorder;
  private sourceBuffer: SourceBuffer | null = null;

  private audioContext: AudioContext | null = null;
  private microphoneStream: MediaStream | null = null;
  private audioWorkletNode: AudioWorkletNode | null = null;

  private playbackQueue: ArrayBuffer[] = [];
  private isAudioPlaying = false;

  private apiUrl = environment.apiUrl;

  isRecording = false;
  transcript = '';

  readonly OPENAI_API_KEY = 'sk-W****'; // 🔐 Your key
  readonly WS_URL = 'wss://api.openai.com/v1/audio/streaming';

  ngOnInit(): void {}

  async startStreaming() {
    const session = await this.createRealtimeSession();
    //if (!sessionUrl) return;

    this.ws = new WebSocket(
      `${environment.openAiRealitimeAgentWSURL}?model=${environment.openAIRealtimeModel}`,
      [
        'realtime',
        'openai-insecure-api-key.' + this.OPENAI_API_KEY,
        'openai-beta.realtime-v1'
      ]
    );
    this.ws.binaryType = 'arraybuffer';

    this.ws.onopen = async () => {
      // This is just a test
      console.log('Realtime Websocke COnnected ............ ');
      const event = {
        type: 'session.update',
        session: {
          modalities: ['text', 'audio'],
          temperature: 0.8,
          instructions: `
            You are a smart, professional, friendly, and empathetic voice assistant working for GFH Investment Bank.

            At the start of every new session, always greet the user with:
            “Thank you for calling GFH Investment Bank. I'm Sara, your AI assistant. How can I assist you today?”

            If the user says “Hi”, “Hello”, or similar greetings later during the session, reply with a friendly short greeting such as:
            “Hello! How can I help you today?”

            Do not repeat the full opening greeting again during the same session.

            Respond naturally and conversationally to general questions about GFH, investments, or the market.

            However, if the user asks anything related to their personal account — such as:
            portfolio size, performance, allocation, expected or actual returns (including CoC), investments, dividends, Wakala, account balance, transactions, CIF number, CMD, or project-specific performance — always trigger a function call.

            Also trigger a function call for questions like:
            - How do I start investing?
            - How can I view my account statement?
            - When will I receive my dividends?
            - Why is my investment not showing?
            - How do I reinvest my dividends?
            - I have not received my dividends yet.

            Never answer such account-related questions directly. Always use the appropriate function call to handle them.

            Language:
            - The conversation must be in English.
            - Do not pass any non-English language, invalid inputs, or garbage characters to function calls.

            Tone and behavior:
            - Maintain a confident, calm, and advisory tone.
            - Show professionalism, patience, and a high degree of financial literacy.
            - Confirm and repeat key details when needed for clarity.

            Limitations:
            - Do not offer financial, legal, or tax advice.
            - Do not disclose client information unless identity is verified.
            - Always follow GFH's compliance and confidentiality policies.

            If a function call is triggered and may take a few seconds to process, say something like “Let me check that for you” or “Just a moment please” to keep the user engaged while waiting.

            Before triggering a function call, verify that the user's input is in English.
            If the input is in any other language or contains random, unreadable, or invalid characters (e.g., emojis, symbols, or scripts like Arabic, Tamil, or Chinese), do not trigger the function call.

            Instead, respond politely with:
            "I'm sorry, I can assist you in English only. Could you please repeat that in English?"

            Never pass non-English or malformed inputs to the function.
          `,
          voice: 'coral',
          input_audio_format: 'pcm16',
          output_audio_format: 'pcm16',
          input_audio_transcription: { model: 'whisper-1' },
          turn_detection: {
            type: 'server_vad',
            threshold: 0.5,
            prefix_padding_ms: 300,
            silence_duration_ms: 200,
            create_response: true
          },
          tools: [
            {
              type: 'function',
              name: 'ask_question',
              description: `
              You are generating responses that will be spoken using a Text-to-Speech (TTS) system.
              The input and output text may contain intentionally modified spellings or formatting (e.g., "You s dee" for USD or "G F H" for GFH) designed to guide pronunciation.
              Do not correct, rephrase, or alter these spellings or formats.
              Your response should preserve all such phonetic or stylistic choices exactly as they appear, as they are essential for correct TTS pronunciation.
              Any word in capital letters and less than 4 charactersm read it with space in between each characters. (e.g., "USD" will be "U S D")
              Please pronounce Roman numerals like I, II, III, IV, etc., in English as 'one', 'two', 'three', 'four' respectively.
              Assume the entire response is being read aloud — clarity, pacing, and pronunciation matter.
              `,
              parameters: {
                type: 'object',
                properties: {
                  question: {
                    type: 'string',
                    description: "User's question"
                  }
                },
                required: ['question']
              }
            }
          ]
        }
      };
      this.ws.send(JSON.stringify(event));

      // Configuration for audio capture
      const audioConstraints = {
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          autoGainControl: true,
          noiseSuppression: true
        },
        video: false
      };

      this.audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)({
        sampleRate: 16000,
        latencyHint: 'interactive'
      });

      // Resume the audio context to avoid browser restrictions
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      // Load the worklet processor
      await this.audioContext.audioWorklet.addModule(
        'assets/pcm-worklet-processor.js'
      );

      // Create the worklet node
      this.audioWorkletNode = new AudioWorkletNode(
        this.audioContext,
        'pcm-processor'
      );

      // Connect the worklet node to the audio context
      this.microphoneStream = await navigator.mediaDevices.getUserMedia(
        audioConstraints
      );
      const source = this.audioContext.createMediaStreamSource(
        this.microphoneStream
      );
      source.connect(this.audioWorkletNode);

      // Handle PCM data from the worklet processor
      this.audioWorkletNode.port.onmessage = event => {
        const pcmData = event.data;

        // Convert the PCM data to a base64-encoded string
        const base64Audio = btoa(
          String.fromCharCode(...new Uint8Array(pcmData))
        );

        // Sending Audio Chunks
        const audioData = {
          type: 'input_audio_buffer.append',
          audio: base64Audio
        };

        // Sending audio data to openai
        this.ws.send(JSON.stringify(audioData));
      };
    };

    this.ws.onmessage = async event => {
      if (typeof event.data === 'string') {
        const msg = JSON.parse(event.data);
        console.log('Received:', msg);

        if (msg.type === 'session.updated') {
          console.log('Session Updated Successfully.', msg);
        } else if (
          msg.type === 'conversation.item.input_audio_transcription.completed'
        ) {
          this.transcript = msg.transcript?.trim() ?? '';

          console.log('User Message ::: ', this.transcript);
        } else if (msg.type === 'response.function_call_arguments.done') {
          // In this case we need to do function call
          // arguments: "{\"question\":\"What is my current portfolio size?\"}"
          const functionCallItemId = msg?.item_id;
          const responseId = msg?.response_id;
          const functionCallarguments = msg?.arguments;
          const question = JSON.parse(functionCallarguments)?.question;

          // Make the API call manually
          const response = await fetch(`${this.apiUrl}/ask_question`, {
            method: 'POST',
            headers: getApiAuthHeaders({
              'Content-Type': 'application/json',
              'X-GFH-AI-CHATBOT-CIF': this.cif,
              'X-GFH-AI-CHATBOT-SESSION-ID': this.currentSessionId
            }),
            body: JSON.stringify({
              question: question,
              session_name: 'default'
            })
          });

          const result = await response.json();
          console.log('Function Call Response:', result);

          this.ws.send(
            JSON.stringify({
              type: 'conversation.item.create',
              item: {
                type: 'function_call_output',
                call_id: msg.call_id,
                output: JSON.stringify({
                  answer: result.answer
                })
              }
            })
          );

          // 2. Trigger the model to continue
          this.ws.send(
            JSON.stringify({
              type: 'response.create'
            })
          );
        } else if (msg.type === 'response.done') {
          const agentTranscript = msg?.response?.output?.[0]?.content?.find(
            (c: any) => c.type === 'audio' && c.transcript
          )?.transcript;

          if (agentTranscript) {
            console.log('🗣️ Agent Final transcript:', agentTranscript);
          }
        } else if (msg.type === 'response.audio.delta') {
          // This will play the audio
          this.handleOpenAIDelta(msg.delta);
        }
      }
    };

    this.ws.onerror = event => {
      console.error('WebSocket error:', event);
    };

    this.ws.onclose = event => {
      console.warn('WebSocket closed:', event);
    };
  }

  async handleOpenAIDelta(base64: string) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    const pcm16 = new Int16Array(bytes.buffer);
    const wav = this.createWavFile(pcm16, 24000); // instead of 16000

    this.playbackQueue.push(wav);
    this.flushAudioQueue();
  }

  private async flushAudioQueue() {
    if (this.isAudioPlaying || !this.playbackQueue.length) return;

    this.isAudioPlaying = true;
    const wav = this.playbackQueue.shift()!;

    try {
      const buffer = await this.audioContext.decodeAudioData(wav.slice(0));
      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(this.audioContext.destination);

      source.onended = () => {
        this.isAudioPlaying = false;
        this.flushAudioQueue();
      };

      source.start();
    } catch (err) {
      console.error('Failed to decode/play audio:', err);
      this.isAudioPlaying = false;
    }
  }

  private createWavFile(pcm16: Int16Array, sampleRate = 16000): ArrayBuffer {
    const numChannels = 1;
    const bitsPerSample = 16;
    const blockAlign = (numChannels * bitsPerSample) / 8;
    const byteRate = sampleRate * blockAlign;
    const wavSize = 44 + pcm16.length * 2;

    const buffer = new ArrayBuffer(wavSize);
    const view = new DataView(buffer);

    this.writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + pcm16.length * 2, true);
    this.writeString(view, 8, 'WAVE');

    this.writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true); // PCM
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitsPerSample, true);

    this.writeString(view, 36, 'data');
    view.setUint32(40, pcm16.length * 2, true);

    for (let i = 0; i < pcm16.length; i++) {
      view.setInt16(44 + i * 2, pcm16[i], true);
    }

    return buffer;
  }

  private writeString(view: DataView, offset: number, str: string) {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  }

  async createRealtimeSession(): Promise<string | null> {
    try {
      const res = await fetch('https://api.openai.com/v1/realtime/sessions', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.OPENAI_API_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'gpt-4o',
          voice: 'ash' // Optional voice config
        })
      });

      const data = await res.json();
      return data?.url ?? null;
    } catch (error) {
      console.error('Failed to create OpenAI realtime session', error);
      return null;
    }
  }

  stopStreaming() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'stop' }));
      this.ws.close();
    }
    if (this.mediaRecorder?.state === 'recording') {
      this.mediaRecorder.stop();
    }
    this.isRecording = false;
  }

  ngOnDestroy(): void {
    this.stopStreaming();
    this.cleanupResources();
  }

  private cleanupResources() {
    if (this.audioWorkletNode) {
      if (this.audioWorkletNode?.port) {
        this.audioWorkletNode.port.close();
      }

      this.audioWorkletNode.disconnect();
      this.audioWorkletNode = null;
    }
    if (this.microphoneStream) {
      this.microphoneStream.getTracks().forEach(track => track.stop());
      this.microphoneStream = null;
    }
  }
}
